import React from 'react';

type Vector = { x: number; y: number };
export const DragArea = ({
    children,
    initialPosition = { x: 0, y: 0 },
    width = 400,
    height = 200,
    onChange,
}: {
    children: React.ReactElement;
    initialPosition: Vector;
    width?: number;
    height?: number;
    onChange: (vec: Vector, mouseIsDown: boolean) => void;
}) => {
    const [mouseIsDown, setMouseDown] = React.useState<boolean>(false);
    const [mouse, setMouse] = React.useState<Vector>({ x: 0, y: 0 });
    const [start, setStart] = React.useState<Vector>({ x: 0, y: 0 });
    const [child, setChild] = React.useState<Vector>(initialPosition);
    const ref = React.useRef<HTMLDivElement>(null);

    function resolveVector(x: number, y: number) {
        let rx = x;
        let ry = y;
        if (x < 0) {
            rx = 0;
        }
        if (y < 0) {
            ry = 0;
        }
        if (x > width) {
            rx = width;
        }
        if (y > height) {
            ry = height;
        }

        return { x: rx, y: ry };
    }

    const handleMouseMove = React.useCallback(
        (event: any) => {
            if (mouseIsDown && ref.current) {
                const dx = start.x + (event.clientX - mouse.x);
                const dy = start.y + (event.clientY - mouse.y);

                setChild(resolveVector(dx, dy));
                onChange(resolveVector(dx, dy), true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mouseIsDown],
    );

    const handleMouseUp = () => {
        setMouseDown(false);
    };

    React.useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove]);

    return (
        <div
            style={{ position: 'relative', width, height }}
            onMouseUp={(event) => {
                setMouseDown(false);
                if (ref.current) {
                    setMouse({ x: event.nativeEvent.clientX, y: event.nativeEvent.clientY });
                    const vector = resolveVector(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
                    setStart(vector);
                    setChild(vector);
                    onChange(vector, false);
                }
            }}
            onMouseDown={(event) => {
                setMouseDown(true);
                if (ref.current) {
                    setMouse({ x: event.nativeEvent.clientX, y: event.nativeEvent.clientY });
                    const vector = resolveVector(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
                    setStart(vector);
                    setChild(vector);
                    onChange(vector, true);
                }
            }}>
            {children}
            <div
                style={{
                    position: 'absolute',
                    top: child.y,
                    left: child.x,
                    pointerEvents: 'none',
                    cursor: '-webkit-grab',
                    backgroundColor: 'transparent',
                    width: 20,
                    height: 20,
                    marginLeft: -10,
                    marginTop: -10,
                }}
                ref={ref}></div>
        </div>
    );
};
